/* eslint-disable max-lines -- cohesive single-feature panel (sheet + editors); splitting
   into separate files would add more navigation overhead than maintenance value. */
import { useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { toast } from "sonner"

import type { DataConnection, SchemaAnnotationsDelta } from "@/api/data-connections-api"
import type { TFunction } from "i18next"

import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  Pencil,
  RefreshCw,
  Table as TableIcon,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"
import { cn } from "@/lib/utils"

import { BulkImportAnnotationsDialog } from "./bulk-import-annotations-dialog"
import {
  useDataConnectionSchemaQuery,
  useUpdateSchemaAnnotationsMutation,
} from "../hooks/use-data-connections"

type Props = {
  connection: DataConnection | null
  onClose: () => void
  onRescan?: (connection: DataConnection) => void
}

/**
 * Preview of the pre-introspected schema catalog for a connection.
 *
 * Opens as a right-hand sheet so the long tree doesn't fight with the
 * admin page. Polls while no catalog is cached yet — a fresh scan lands
 * in-view within 10-30 seconds on typical datasets without a hard
 * refresh.
 */
export function ConnectionSchemaSheet({ connection, onClose, onRescan }: Props) {
  const { t } = useTranslation()
  const { data, isLoading, error, refetch } = useDataConnectionSchemaQuery(
    connection?.id,
    !!connection
  )
  const saveAnnotation = useUpdateSchemaAnnotationsMutation()
  const [bulkImportOpen, setBulkImportOpen] = useState(false)

  const saveDelta = async (delta: SchemaAnnotationsDelta): Promise<void> => {
    if (!connection) return
    await saveAnnotation.mutateAsync({ id: connection.id, delta })
  }

  const totals = useMemo(() => {
    const schemas = (data?.schemas ?? {}) as Record<
      string,
      { tables?: Record<string, { columns?: unknown[] }> }
    >
    let tables = 0
    let columns = 0
    for (const s of Object.values(schemas)) {
      const tableEntries = Object.values(s.tables ?? {})
      tables += tableEntries.length
      for (const tbl of tableEntries) {
        columns += (tbl.columns ?? []).length
      }
    }
    return { tables, columns, schemas: Object.keys(schemas).length }
  }, [data])

  return (
    <Sheet open={!!connection} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl lg:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Database className="size-5" />
            {t("adminDashboard.dataConnections.schemaSheet.title", "Schema catalog")}
            {connection && (
              <span className="text-muted-foreground font-mono text-sm font-normal">
                ({connection.id})
              </span>
            )}
          </SheetTitle>
          <SheetDescription>
            {t(
              "adminDashboard.dataConnections.schemaSheet.description",
              "What the Dash agent sees when it calls sql_schema_overview."
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <StatusBar
            data={data}
            error={error}
            isLoading={isLoading}
            totals={totals}
            onBulkImport={data ? () => setBulkImportOpen(true) : undefined}
            onRefetch={() => void refetch()}
            onRescan={connection && onRescan ? () => onRescan(connection) : undefined}
          />

          <BulkImportAnnotationsDialog
            catalog={data}
            open={bulkImportOpen}
            onApply={saveDelta}
            onOpenChange={setBulkImportOpen}
          />

          {!isLoading && !data && !error && (
            <EmptyState
              icon={Database}
              description={t(
                "adminDashboard.dataConnections.schemaSheet.notScannedDescription",
                "Click 'Scan schema' on the connection to build the catalog. A scan typically takes 30 seconds to 3 minutes."
              )}
              title={t(
                "adminDashboard.dataConnections.schemaSheet.notScannedTitle",
                "Catalog not scanned yet"
              )}
            />
          )}

          {data && (
            <EditableProse
              key={`summary-${connection?.id}`}
              dirtyKey={`connection-summary:${connection?.id ?? "unknown"}`}
              value={data.summary ?? null}
              label={t(
                "adminDashboard.dataConnections.schemaSheet.summaryLabel",
                "Connection notes (optional)"
              )}
              placeholder={t(
                "adminDashboard.dataConnections.schemaSheet.summaryPlaceholder",
                "E.g. amounts in SEK incl VAT, negatives = refunds. Shared across all schemas."
              )}
              onSave={(next) => saveDelta({ summary: next })}
            />
          )}

          {data && data.schemas && (
            <div className="space-y-2">
              {Object.entries(data.schemas).map(([schemaName, schemaEntry]) => (
                <SchemaTree
                  key={schemaName}
                  entry={schemaEntry as SchemaNode}
                  name={schemaName}
                  onSaveDelta={saveDelta}
                />
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

type StatusBarProps = {
  data: ReturnType<typeof useDataConnectionSchemaQuery>["data"]
  isLoading: boolean
  error: Error | null
  onRefetch: () => void
  onRescan: (() => void) | undefined
  onBulkImport: (() => void) | undefined
  totals: { schemas: number; tables: number; columns: number }
}

function StatusBar({
  data,
  isLoading,
  error,
  onRefetch,
  onRescan,
  onBulkImport,
  totals,
}: StatusBarProps) {
  const { t } = useTranslation()
  const status = data?.status
  const timestamp = data?.introspected_at ? formatTimestamp(data.introspected_at) : null
  const statusLabel = resolveStatusLabel(isLoading, status, t)

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border p-3">
      <div className="flex items-center gap-2 text-sm">
        {isLoading && <Spinner className="size-4" />}
        {!isLoading && status === "success" && <CheckCircle2 className="text-success size-4" />}
        {!isLoading && status === "partial" && <AlertCircle className="text-warning-ink size-4" />}
        {!isLoading && status === "failed" && <AlertCircle className="text-destructive size-4" />}
        <div className="flex flex-col">
          <span className="font-medium">{statusLabel}</span>
          {data && (
            <span className="text-muted-foreground text-xs">
              {totals.schemas} {t("adminDashboard.dataConnections.schemaSheet.schemas", "schemas")}{" "}
              · {totals.tables} {t("adminDashboard.dataConnections.schemaSheet.tables", "tables")} ·{" "}
              {totals.columns} {t("adminDashboard.dataConnections.schemaSheet.columns", "columns")}
              {timestamp && ` · ${timestamp}`}
            </span>
          )}
          {data?.error && <span className="text-destructive text-xs">{data.error}</span>}
          {error && <span className="text-destructive text-xs">{error.message}</span>}
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onRefetch}>
          <RefreshCw className="mr-1 size-4" />
          {t("adminDashboard.dataConnections.schemaSheet.refetch", "Refetch")}
        </Button>
        {onBulkImport && (
          <Button size="sm" variant="outline" onClick={onBulkImport}>
            <FileText className="mr-1 size-4" />
            {t("adminDashboard.dataConnections.schemaSheet.bulkImport", "Import text")}
          </Button>
        )}
        {onRescan && (
          <Button size="sm" variant="outline" onClick={onRescan}>
            <Database className="mr-1 size-4" />
            {t("adminDashboard.dataConnections.schemaSheet.rescan", "Rescan")}
          </Button>
        )}
      </div>
    </div>
  )
}

type ColumnNode = {
  name: string
  type: string
  nullable?: boolean
  example_values?: string[]
  distinct_count_estimate?: number | null
  description?: string | null
}

type TableNode = {
  row_count_estimate?: number | null
  description?: string | null
  columns?: ColumnNode[]
}

type SchemaNode = {
  description?: string | null
  tables?: Record<string, TableNode>
}

type SaveDeltaFn = (delta: SchemaAnnotationsDelta) => Promise<void>

function SchemaTree({
  name,
  entry,
  onSaveDelta,
}: {
  name: string
  entry: SchemaNode
  onSaveDelta: SaveDeltaFn
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)
  const tables = entry.tables ?? {}
  const tableCount = Object.keys(tables).length

  return (
    <div className="rounded-md border">
      <button
        aria-expanded={open}
        className="hover:bg-muted/60 flex w-full items-center justify-between rounded-t-md px-3 py-2"
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          <Database className="size-4" />
          {name}
        </span>
        <span className="text-muted-foreground text-xs">
          {tableCount} {t("adminDashboard.dataConnections.schemaSheet.tables", "tables")}
        </span>
      </button>
      <div className="px-3 pt-2">
        <EditableInline
          dirtyKey={`schema-description:${name}`}
          value={entry.description ?? null}
          placeholder={t(
            "adminDashboard.dataConnections.schemaSheet.schemaDescPlaceholder",
            "One-line description of this schema for the Dash agent"
          )}
          onSave={(next) => onSaveDelta({ schemas: { [name]: { description: next } } })}
        />
      </div>
      {open && (
        <div className="border-t">
          {Object.entries(tables).map(([tableName, tableEntry]) => (
            <TableRow
              key={tableName}
              entry={tableEntry}
              name={tableName}
              schemaName={name}
              onSaveDelta={onSaveDelta}
            />
          ))}
          {tableCount === 0 && (
            <p className="text-muted-foreground px-3 py-2 text-xs">
              {t("adminDashboard.dataConnections.schemaSheet.noTables", "No tables")}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function TableRow({
  name,
  entry,
  schemaName,
  onSaveDelta,
}: {
  name: string
  entry: TableNode
  schemaName: string
  onSaveDelta: SaveDeltaFn
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const columns = entry.columns ?? []
  return (
    <div className="border-b last:border-b-0">
      <button
        aria-expanded={open}
        className="hover:bg-muted/50 flex w-full items-center justify-between px-3 py-2"
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-2 text-sm">
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          <TableIcon className="text-muted-foreground size-4" />
          <span className="font-mono">{name}</span>
        </span>
        <span className="text-muted-foreground text-xs">
          {columns.length} {t("adminDashboard.dataConnections.schemaSheet.columns", "columns")}
          {entry.row_count_estimate != null && (
            <>
              {" · "}
              {entry.row_count_estimate.toLocaleString()}{" "}
              {t("adminDashboard.dataConnections.schemaSheet.rows", "rows")}
            </>
          )}
        </span>
      </button>
      <div className="px-3 pb-2">
        <EditableInline
          dirtyKey={`table-description:${schemaName}:${name}`}
          value={entry.description ?? null}
          placeholder={t(
            "adminDashboard.dataConnections.schemaSheet.tableDescPlaceholder",
            "One-line description of this table for the Dash agent"
          )}
          onSave={(next) =>
            onSaveDelta({
              schemas: { [schemaName]: { tables: { [name]: { description: next } } } },
            })
          }
        />
      </div>
      {open && columns.length > 0 && (
        <div className="bg-muted/20 px-3 py-2">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground">
                <th className="py-1 text-left font-medium">
                  {t("adminDashboard.dataConnections.schemaSheet.colName", "Column")}
                </th>
                <th className="py-1 text-left font-medium">
                  {t("adminDashboard.dataConnections.schemaSheet.colType", "Type")}
                </th>
                <th className="py-1 text-left font-medium">
                  {t("adminDashboard.dataConnections.schemaSheet.colDescription", "Description")}
                </th>
                <th className="py-1 text-left font-medium">
                  {t("adminDashboard.dataConnections.schemaSheet.colExamples", "Example values")}
                </th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col) => (
                <tr key={col.name} className="border-muted-foreground/10 border-t align-top">
                  <td className="py-1 pr-2 font-mono">
                    {col.name}
                    {col.nullable === false && (
                      <span className="text-muted-foreground ml-1 text-[10px] uppercase">
                        · not null
                      </span>
                    )}
                  </td>
                  <td className="text-muted-foreground py-1 pr-2">{col.type}</td>
                  <td className="py-1 pr-2">
                    <EditableInline
                      compact
                      dirtyKey={`column-description:${schemaName}:${name}:${col.name}`}
                      value={col.description ?? null}
                      placeholder={t(
                        "adminDashboard.dataConnections.schemaSheet.colDescPlaceholder",
                        "One-line description"
                      )}
                      onSave={(next) =>
                        onSaveDelta({
                          schemas: {
                            [schemaName]: {
                              tables: {
                                [name]: {
                                  columns: { [col.name]: { description: next } },
                                },
                              },
                            },
                          },
                        })
                      }
                    />
                  </td>
                  <td className="py-1">
                    {col.example_values && col.example_values.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {col.example_values.slice(0, 6).map((v, i) => (
                          <span
                            key={i}
                            className={cn("bg-muted rounded px-1.5 py-0.5 font-mono text-[11px]")}
                          >
                            {v}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function EditableInline({
  value,
  onSave,
  placeholder,
  compact,
  dirtyKey,
}: {
  value: string | null
  onSave: (next: string | null) => Promise<void>
  placeholder?: string
  compact?: boolean
  dirtyKey: string
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? "")
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const dirty = draft !== (value ?? "")
  useRegisterAdminDirty(dirtyKey, editing && dirty)

  useEffect(() => {
    if (!editing) setDraft(value ?? "")
  }, [value, editing])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const commit = async () => {
    const trimmed = draft.trim()
    const next = trimmed.length === 0 ? null : trimmed
    if ((value ?? null) === next) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(next)
      setEditing(false)
    } catch (err) {
      // Surface the save failure — the previous finally-only path left
      // the user with a clearing spinner and no indication the write
      // didn't persist.
      toast.error(
        err instanceof Error
          ? err.message
          : t("adminDashboard.dataConnections.schemaSheet.saveFailed", "Failed to save description")
      )
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={cn(
          "group text-muted-foreground hover:text-foreground flex w-full items-center gap-1 text-left",
          compact ? "text-[11px]" : "text-xs"
        )}
        onClick={(e) => {
          e.stopPropagation()
          setEditing(true)
        }}
      >
        <Pencil className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
        <span className={cn("truncate", !value && "italic")}>
          {value ??
            placeholder ??
            t("adminDashboard.dataConnections.schemaSheet.addDescription", "Add description…")}
        </span>
      </button>
    )
  }

  return (
    <div
      className="flex items-center gap-1"
      role="presentation"
      onClick={(e) => e.stopPropagation()}
    >
      <Input
        ref={inputRef}
        className={cn("h-7 text-xs", compact && "text-[11px]")}
        disabled={saving}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            void commit()
          } else if (e.key === "Escape") {
            setDraft(value ?? "")
            setEditing(false)
          }
        }}
      />
      <Button
        aria-label="Save"
        disabled={saving}
        size="icon"
        type="button"
        variant="ghost"
        onClick={() => void commit()}
      >
        {saving ? <Spinner className="size-3" /> : <Check className="size-3" />}
      </Button>
      <Button
        aria-label="Cancel"
        disabled={saving}
        size="icon"
        type="button"
        variant="ghost"
        onClick={() => {
          setDraft(value ?? "")
          setEditing(false)
        }}
      >
        <X className="size-3" />
      </Button>
    </div>
  )
}

function EditableProse({
  value,
  onSave,
  label,
  placeholder,
  dirtyKey,
}: {
  value: string | null
  onSave: (next: string | null) => Promise<void>
  label: string
  placeholder?: string
  dirtyKey: string
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? "")
  const [saving, setSaving] = useState(false)
  const dirty = draft !== (value ?? "")
  useRegisterAdminDirty(dirtyKey, editing && dirty)

  useEffect(() => {
    if (!editing) setDraft(value ?? "")
  }, [value, editing])

  const commit = async () => {
    const trimmed = draft.trim()
    const next = trimmed.length === 0 ? null : trimmed
    if ((value ?? null) === next) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(next)
      setEditing(false)
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t("adminDashboard.dataConnections.schemaSheet.saveFailed", "Failed to save description")
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        {editing ? (
          <div className="flex gap-2">
            <Button
              disabled={!dirty || saving}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => void commit()}
            >
              {saving ? <Spinner className="mr-1 size-3" /> : <Check className="mr-1 size-3" />}
              {t("common.save", "Save")}
            </Button>
            <Button
              disabled={saving}
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft(value ?? "")
                setEditing(false)
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
          </div>
        ) : (
          <Button size="sm" type="button" variant="ghost" onClick={() => setEditing(true)}>
            <Pencil className="mr-1 size-3" />
            {t("common.edit", "Edit")}
          </Button>
        )}
      </div>
      {editing && (
        <Textarea
          className="font-mono text-xs"
          placeholder={placeholder}
          rows={14}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      )}
      {!editing && value && (
        <div className="prose prose-sm dark:prose-invert max-w-none text-xs">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
        </div>
      )}
      {!editing && !value && (
        <p className="text-muted-foreground text-xs italic">
          {placeholder ??
            t(
              "adminDashboard.dataConnections.schemaSheet.summaryEmpty",
              "No notes yet — click Edit to add context the Dash agent should always know."
            )}
        </p>
      )}
    </div>
  )
}

function resolveStatusLabel(
  isLoading: boolean,
  status: "success" | "partial" | "failed" | undefined,
  t: TFunction
): string {
  if (isLoading) {
    return t("adminDashboard.dataConnections.schemaSheet.statusLoading", "Loading…")
  }
  if (status) {
    return t(`adminDashboard.dataConnections.schemaSheet.status.${status}`, {
      defaultValue: status,
    })
  }
  return t("adminDashboard.dataConnections.schemaSheet.statusUnknown", "No catalog yet")
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString()
  } catch {
    return iso
  }
}
